import { createApp, h } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNuxtApp, useFetch, useRuntimeConfig } from '#app';

// Import all components

import Image from '~/components/shortcodes/Image.vue';
import VueLink from '~/components/shortcodes/VueLink.vue';
import IntroDialog from '~/components/shortcodes/IntroDialog.vue';
import IrIntroDialog from '~/components/shortcodes/IrIntroDialog.vue';
import Banner from '~/components/shortcodes/Banner.vue';
// import Lightbox from '~/components/shortcodes/Lightbox.vue';
import Echart from '~/components/shortcodes/Echart.vue';
import EchartShareholder from '~/components/shortcodes/EchartShareholder.vue';
import NewsRoom from '~/components/shortcodes/NewsRoom.vue';

import Document from '~/components/shortcodes/Document.vue';
import Accordion from '~/components/shortcodes/Accordion.vue';
import Contact from '~/components/shortcodes/form/Contact.vue';
import IrContact from '~/components/shortcodes/form/IrContact.vue';
import DocumentContact from '~/components/shortcodes/form/DocumentContact.vue';
import LatestSustainabilityReport from '~/components/shortcodes/downloads/LatestSustainabilityReport.vue';
import SustainabilityUpdate from '~/components/shortcodes/update/SustainabilityUpdate.vue';
import JobBoard from '~/components/shortcodes/JobBoard.vue';
import Button from '~/components/shortcodes/Button.vue';

import HeroBanner from '~/components/shortcodes/home/HeroBanner.vue';
import StockHome from '~/components/shortcodes/home/Stock.vue';
import Update from '~/components/shortcodes/home/Update.vue';
import VideoHome from '~/components/shortcodes/home/Video.vue';

import IrHeroBanner from '~/components/shortcodes/ir-home/HeroBanner.vue';
import QuickDownload from '~/components/shortcodes/ir-home/QuickDownload.vue';
import Newsroom from '~/components/shortcodes/ir-home/Newsroom.vue';
import DocumentTable from '~/components/shortcodes/DocumentTable.vue';
import StockQuote from '~/components/shortcodes/stock/Quote.vue';
import Sitemap from '~/components/shortcodes/Sitemap.vue';
import videoPopup from '~/components/shortcodes/VideoPopup.vue';
import Gridtab from '~/components/shortcodes/Gridtab.vue';
import WorkVideoPopup from '~/components/shortcodes/WorkVideoPopup.vue';

import EmailAlerts from '~/components/shortcodes/form/EmailAlerts.vue';
import EmailAlertsUnsubscribe from '~/components/shortcodes/form/EmailAlertsUnsubscribe.vue';
import Whistleblowing from '~/components/shortcodes/form/Whistleblowing.vue';
import ResizableIframe from '~/components/shortcodes/ResizableIframe.vue';

import SustainabilityActivity from '../components/shortcodes/update/SustainabilityActivity.vue';


export const useShortcodeParser = () => {
  
  const { locale } = useI18n();
  const config = useRuntimeConfig();
  const baseApi = config.public.baseApi;
  const appApi = config.public.appApi;
  const { $router } = useNuxtApp();

  // Cache storage for parsed shortcodes
  const cache = new Map();

  const shortcodeRegistry = {
    // 'lightbox': { 
    //   component: Lightbox,
    // },
    'vuelink': { 
      component: VueLink,
    },
    'banner': { 
      component: Banner,
    },
    'image': { 
      component: Image,
    },
    'intro-dialog': {
      component: IntroDialog,
      fetchData: async (attrs) => {
        const cacheKey = `intro-dialog-${locale.value}`;
        if (cache.has(cacheKey)) {
          return cache.get(cacheKey);
        }

        const { data } = await useFetch(`${baseApi}/${locale.value}/intro-dialog`);
        const result = { 
          posts: data?.value?.data || [],
          ...attrs
        };
        
        // Cache the result
        cache.set(cacheKey, result);
        return result;
      },
    },
    'ir-intro-dialog': {
      component: IntroDialog,
      fetchData: async (attrs) => {
        const cacheKey = `ir-intro-dialog-${locale.value}`;
        if (cache.has(cacheKey)) {
          return cache.get(cacheKey);
        }

        const { data } = await useFetch(`${baseApi}/${locale.value}/ir-intro-dialog`);
        const result = { 
          posts: data?.value?.data || [],
          ...attrs
        };
        
        // Cache the result
        cache.set(cacheKey, result);
        return result;
      },
    },
    'document': {
      component: Document,
      fetchData: async (attrs) => {
        const category = attrs.category;
        const { data } = await useFetch(`${baseApi}/${locale.value}/posts/documents/${category}`);
        return { posts: data?.value?.data || [], ...attrs };
      },
    },
    'accordion': {
      component: Accordion,
      fetchData: async (attrs) => {
        const category = attrs.category;
        const { data } = await useFetch(`${baseApi}/${locale.value}/posts/accordions/${category}`);
        return { posts: data?.value?.data || [], ...attrs };
      },
    },
    'contact': { 
      component: Contact,
    },
    'ir-contact': { 
      component: IrContact,
    },
    'document-contact': { 
      component: DocumentContact,
    },
    'hero-banner': {
      component: HeroBanner,
      fetchData: async (attrs) => {
        const showTitle = attrs.title == 'false' ? false : true;
        const category = attrs.category ?? 'hero-banner';
        const { data: heroBannerData } = await useFetch(`${baseApi}/${locale.value}/posts/${category}`);
        return { posts: heroBannerData?.value?.data || [], ...attrs, showTitle };
      },
    },
    'stock-home': {
      component: StockHome,
      fetchData: async (attrs) => {
        const { data: stockData } = await useFetch(`${baseApi}/${locale.value}/ir/stock`);
        return { stock: stockData?.value?.data || [], ...attrs };
      },
    },
    'updates': {
      component: Update,
      fetchData: async (attrs) => {
        const limit = attrs.limit ?? '3';
        const category = attrs.category;
        const { data } = await useFetch(`${baseApi}/${locale.value}/posts/shortcode/updates/${category}?limit=${limit}`);
        return { posts: data?.value?.data || [], ...attrs };
      },
    },
    'video-home': {
      component: VideoHome,
      fetchData: async (attrs) => {
        const src = attrs.src;
        return { ...attrs };
      },
    },
    'sd-report-last': {
      component: LatestSustainabilityReport,
      fetchData: async (attrs) => {
        const limit = attrs.limit ?? '1';
        const { data: sdReportLast } = await useFetch(`${baseApi}/${locale.value}/ir/downloads/sustainable-development-report?limit=${limit}`);
        return { posts: sdReportLast?.value?.data || [], limit, ...attrs };
      },
    },
    'sd-updates': {
      component: SustainabilityUpdate,
      fetchData: async (attrs) => {
        const limit = attrs.limit ?? '3';
        const { data: sdUpdate } = await useFetch(`${baseApi}/${locale.value}/posts/shortcode/updates/csr-activities?limit=${limit}`);
        console.log('sdUpdate')
        console.log(sdUpdate?.value?.data)
        return { posts: sdUpdate?.value?.data || [], ...attrs };
      },
    },
    'sd-activity': {
      component: SustainabilityActivity,
      fetchData: async (attrs) => {
        const limit = attrs.limit ?? '6';
        const { data: esgUpdate } = await useFetch(`${baseApi}/${locale.value}/posts/shortcode/updates/sustainability/sustainable-activities?limit=${limit}`);
        return { posts: esgUpdate?.value?.data || [], ...attrs };
      },
    },
    'sd-hero-banner': {
      component: HeroBanner,
      fetchData: async (attrs) => {
        const showTitle = attrs.title == 'false' ? false : true;
        const tag = attrs.tag ?? 'overview';
        const { data: sdHeroBannerData } = await useFetch(`${baseApi}/${locale.value}/posts/sd-hero-banner?tag=${tag}`);
        return { posts: sdHeroBannerData?.value?.data || [], ...attrs, showTitle };
      },
    },
    'jobboard-hero-banner': {
      component: HeroBanner,
      fetchData: async (attrs) => {
        const showTitle = attrs.title == 'false' ? false : true;
        const { data: sdHeroBannerData } = await useFetch(`${baseApi}/${locale.value}/posts/jobboard-hero-banner`);
        return { posts: sdHeroBannerData?.value?.data || [], ...attrs, showTitle };
      },
    },
    'jobboard': {
      component: JobBoard,
      fetchData: async (attrs) => {
        const { data: jobBoardData } = await useFetch(`${baseApi}/${locale.value}/posts/jobboard`);
        return { locations: jobBoardData?.value?.data?.location || [],departments: jobBoardData?.value?.data?.department || [],posts: jobBoardData?.value?.data?.posts || [], ...attrs };
      },
    },
    'button': { component: Button },
    'ir-hero-banner': {
      component: IrHeroBanner,
      fetchData: async (attrs) => {
        const category = attrs.category ?? 'ir-hero-banner';
        const { data } = await useFetch(`${baseApi}/${locale.value}/posts/ir-hero-banner`);
        return { posts: data?.value?.data || [], ...attrs };
      },
    },
    'newsroom': {
      component: Newsroom,
      fetchData: async (attrs) => {
        const { data } = await useFetch(`${baseApi}/${locale.value}/ir/newsroom/highlight`);
        return { posts: data?.value?.data || [], ...attrs };
      },
    },
    'quick-download': {
      component: QuickDownload,
      fetchData: async (attrs) => {
        const { data } = await useFetch(`${baseApi}/${locale.value}/ir/downloads/highlights`);
        return { posts: data?.value?.data || [], ...attrs };
      },
    },
    'ir-document-table': {
      component: DocumentTable,
      fetchData: async (attrs) => {
        const category = attrs.category ?? 'ir-hero-banner';
        const order = attrs.order ?? 'false';
        const { data } = await useFetch(`${baseApi}/${locale.value}/ir/posts/documents/${category}?year=all&order=${order}`);
        return { posts: data?.value?.data || [], ...attrs };
      },
    },
    'stock-quote': {
      component: StockQuote,
      fetchData: async (attrs) => {
        const { data: stockData } = await useFetch(`${baseApi}/${locale.value}/ir/stock`);
        return { stock: stockData?.value?.data || [], ...attrs };
      },
    },
    'email-alerts': {
      component: EmailAlerts,
      fetchData: async () => {
        const { data } = await useFetch(`${baseApi}/${locale.value}/ir/email-alerts`);
        return { posts: data?.value?.data || [] };
      },
    },
    'email-alerts-unsubscribe': {
      component: EmailAlertsUnsubscribe,
      fetchData: async () => {
        const { data } = await useFetch(`${baseApi}/${locale.value}/ir/email-alerts`);
        return { posts: data?.value?.data || [] };
      },
    },
    'echart': {
      component: Echart,
      fetchData: async (attrs) => {
        const category = attrs.category;
        const templateType = attrs.template || 'Bar';
        const { data } = await useFetch(`${baseApi}/${locale.value}/ir/posts/chart/${category}`);
        return { post: data?.value?.data || [], ...attrs };
      },
    },
    'echart-shareholder': {
      component: EchartShareholder
    },
    'whistleblowing': { component: Whistleblowing },
    'sitemap': {
      component: Sitemap,
      fetchData: async () => {
        const { data } = await useFetch(`${baseApi}/${locale.value}/menus/main`);
        return { menus: data?.value?.data || [] };
      },
    },
    'iframe': { component: ResizableIframe },
    'video-popup': { component: videoPopup },
    'gridtab': {
      component: Gridtab,
      fetchData: async (attrs) => {
        const category = attrs.category;
        const { data } = await useFetch(`${baseApi}/${locale.value}/posts/gridtab/${category}`);
        return { posts: data?.value?.data || [], ...attrs };
      },
    },
    'work-video-popup': { component: WorkVideoPopup },
    
  };

  const parseShortcodes = async (content) => {
    // Regex to match both [shortcode] and [shortcode][/shortcode]
    const regex = /\[([\w-]+)(.*?)\](?:([\s\S]*?)\[\/\1\])?/g;
    let match;
    let parsedContent = content;

    while ((match = regex.exec(content)) !== null) {
      const shortcode = match[1];
      const attrString = match[2];
      const innerContent = match[3] || ""; // Content between opening and closing shortcode tags

      // Parse attributes
      const attrs = {};
      const attrRegex = /(\w+)="(.*?)"/g;
      let attrMatch;
      while ((attrMatch = attrRegex.exec(attrString)) !== null) {
        attrs[attrMatch[1]] = attrMatch[2];
      }

      const entry = shortcodeRegistry[shortcode];
      if (!entry) {
        console.warn(`Shortcode not registered: ${shortcode}`);
        continue;
      }

      // Generate a unique ID for the container
      const containerId = `${shortcode}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      parsedContent = parsedContent.replace(
        match[0],
        `<div id="${containerId}" class="shortcode-container"></div>`
      );

      // Render the component
      setTimeout(async () => {
        const container = document.getElementById(containerId);
        if (container) {
          // Cache data for this shortcode
          const cacheKey = `${shortcode}-${JSON.stringify(attrs)}`;
          let props;
          if (cache.has(cacheKey)) {
            props = cache.get(cacheKey);
          } else {
            props = entry.fetchData ? await entry.fetchData(attrs) : attrs;
            cache.set(cacheKey, props);
          }

          // Include inner content as a prop if supported by the component
          props.innerContent = innerContent;

          const app = createApp(entry.component, props);
          app.use($router);
          app.mount(container);
        }
      }, 0);



    }
  
    return parsedContent;
  };

  return { parseShortcodes };
};